import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Star, ShoppingBag, ExternalLink, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';

const ProductRecommendationCard = ({ product }) => {
  const { isAuthenticated } = useAuth();
  const { addToCart } = useCart();
  const [isAdding, setIsAdding] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0
    }).format(price);
  };

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isAuthenticated) {
      toast.error('Inicia sesión para agregar al carrito');
      return;
    }

    if (product.stock <= 0) {
      toast.error('Producto agotado');
      return;
    }

    try {
      setIsAdding(true);
      await addToCart(product.id, 1);
      toast.success('Agregado al carrito');
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al agregar al carrito';
      toast.error(message);
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <div 
      className="bg-white rounded-2xl border overflow-hidden transition-all duration-400 ease-out group"
      style={{
        borderColor: isHovered ? 'rgba(186,123,124,0.3)' : 'rgba(231,229,228,1)',
        boxShadow: isHovered 
          ? '0 12px 32px rgba(186,123,124,0.15), 0 4px 12px rgba(0,0,0,0.08)' 
          : '0 2px 8px rgba(0,0,0,0.04)',
        transform: isHovered ? 'translateY(-2px)' : 'translateY(0)'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-testid={`product-card-${product.id}`}
    >
      {/* Product Image */}
      <div className="relative aspect-square bg-gradient-to-br from-[#FDF8F6] to-[#F5F0EB] overflow-hidden">
        <img
          src={product.image_url}
          alt={product.name}
          className="w-full h-full object-cover transition-transform duration-600 ease-out"
          style={{
            transform: isHovered ? 'scale(1.08)' : 'scale(1)'
          }}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIyMDAiIGZpbGw9IiNGNUYwRUIiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iI0E4QTI5RSIgZm9udC1mYW1pbHk9InNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTQiPlByb2R1Y3RvPC90ZXh0Pjwvc3ZnPg==';
          }}
        />
        
        {/* Shimmer overlay on hover */}
        <div 
          className="absolute inset-0 pointer-events-none transition-opacity duration-400"
          style={{
            background: 'linear-gradient(135deg, transparent 40%, rgba(255,255,255,0.3) 50%, transparent 60%)',
            opacity: isHovered ? 1 : 0,
            transform: 'translateX(-100%)',
            animation: isHovered ? 'shimmer 1.5s ease-in-out' : 'none'
          }}
        />
        
        {/* Featured Badge */}
        {product.featured && (
          <div 
            className="absolute top-2.5 left-2.5 text-white text-xs px-2.5 py-1.5 rounded-full flex items-center space-x-1 shadow-md"
            style={{
              background: 'linear-gradient(135deg, #D4A5A5 0%, #BA7B7C 100%)'
            }}
          >
            <Sparkles className="w-3 h-3" />
            <span className="font-medium">Destacado</span>
          </div>
        )}
        
        {/* Stock Badge */}
        {product.stock <= 0 && (
          <div className="absolute top-2.5 right-2.5 bg-red-500 text-white text-xs px-2.5 py-1.5 rounded-full font-medium shadow-md">
            Agotado
          </div>
        )}
        {product.stock > 0 && product.stock <= 3 && (
          <div className="absolute top-2.5 right-2.5 bg-amber-500 text-white text-xs px-2.5 py-1.5 rounded-full font-medium shadow-md">
            ¡Últimos {product.stock}!
          </div>
        )}
      </div>
      
      {/* Product Info */}
      <div className="p-4">
        <p className="text-xs tracking-wider uppercase text-[#A8A29E] mb-1.5">
          {product.category}
        </p>
        <h4 
          className="text-base text-[#1C1917] mb-2 line-clamp-2 transition-colors duration-200"
          style={{ 
            fontFamily: "'Cormorant Garamond', serif",
            fontWeight: isHovered ? '500' : '400'
          }}
        >
          {product.name}
        </h4>
        
        {/* Rating */}
        {product.review_count > 0 && (
          <div className="flex items-center space-x-1.5 mb-2">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className={`w-3.5 h-3.5 transition-colors duration-200 ${
                    i < Math.round(product.average_rating) 
                      ? 'text-[#BA7B7C] fill-[#BA7B7C]' 
                      : 'text-[#E7E5E4]'
                  }`}
                />
              ))}
            </div>
            <span className="text-xs text-[#A8A29E]">({product.review_count})</span>
          </div>
        )}
        
        {/* Price */}
        <p 
          className="text-lg font-semibold mb-3.5 transition-colors duration-200"
          style={{ color: isHovered ? '#A85D5E' : '#BA7B7C' }}
        >
          {formatPrice(product.price)} MXN
        </p>
        
        {/* Actions */}
        <div className="flex gap-2.5">
          <Link
            to={`/product/${product.id}`}
            className="flex-1 flex items-center justify-center space-x-1.5 py-2.5 px-3 bg-[#F5F0EB] text-[#1C1917] rounded-xl text-sm font-medium hover:bg-[#E7E5E4] transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
            data-testid={`view-product-${product.id}`}
          >
            <ExternalLink className="w-3.5 h-3.5" />
            <span>Ver</span>
          </Link>
          <button
            onClick={handleAddToCart}
            disabled={isAdding || product.stock <= 0}
            className="flex-1 flex items-center justify-center space-x-1.5 py-2.5 px-3 text-white rounded-xl text-sm font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:scale-[1.02] active:scale-[0.98]"
            style={{
              background: 'linear-gradient(135deg, #D4A5A5 0%, #BA7B7C 100%)',
              boxShadow: isHovered && !isAdding && product.stock > 0 
                ? '0 4px 12px rgba(186,123,124,0.35)' 
                : 'none'
            }}
            data-testid={`add-cart-${product.id}`}
          >
            {isAdding ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <ShoppingBag className="w-3.5 h-3.5" />
                <span>Agregar</span>
              </>
            )}
          </button>
        </div>
      </div>
      
      {/* Add shimmer animation keyframes */}
      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(200%); }
        }
      `}</style>
    </div>
  );
};

export default ProductRecommendationCard;
